import React, {useState} from 'react'
import {Spinner} from 'react-bootstrap'
import {TaskItemWithId} from '../Tasks'
import {formatDateTime, resolveContactLabel, formatTaskInitials, STATUS_META} from '../taskHelpers'
import clsx from 'clsx'
import './TaskMobileCard.scss'

interface TaskMobileCardProps {
  task: TaskItemWithId
  isSelected: boolean
  onSelect: (id: string) => void
  onEdit: (task: TaskItemWithId) => void
  onDuplicate?: (task: TaskItemWithId) => void
  onToggleStatus: (task: TaskItemWithId) => void
  onDelete: (task: TaskItemWithId) => void
  isProcessing?: boolean
}

const CONTACT_COLOR_CLASSES = [
  'bg-light-primary text-primary',
  'bg-light-success text-success',
  'bg-light-warning text-warning',
  'bg-light-info text-info',
  'bg-light-danger text-danger',
  'bg-light-dark text-dark',
]

const circleBadgeStyle = {
  width: '32px',
  height: '32px',
  fontSize: '0.75rem',
} as const

const MAX_VISIBLE_CONTACTS = 2

export const TaskMobileCard: React.FC<TaskMobileCardProps> = ({
  task,
  isSelected,
  onSelect,
  onEdit,
  onDuplicate,
  onToggleStatus,
  onDelete,
  isProcessing = false,
}) => {
  const [showActions, setShowActions] = useState(false)

  const handleActionClick = () => {
    setShowActions(!showActions)
  }

  const statusMeta = STATUS_META[task.status]
  const isOverdue =
    task.status !== 'completed' && task.dueDate
      ? new Date(task.dueDate) < new Date()
      : false

  const contacts = Array.isArray(task.contacts) ? task.contacts : []
  const visibleContacts = contacts.slice(0, MAX_VISIBLE_CONTACTS)
  const remaining = contacts.length - MAX_VISIBLE_CONTACTS

  return (
    <div className={`task-mobile-card ${isSelected ? 'selected' : ''}`}>
      {/* Header with checkbox and title */}
      <div className='card-header-section'>
        <div className='d-flex align-items-center gap-3 flex-grow-1'>
          <input
            type='checkbox'
            className='form-check-input task-checkbox'
            checked={isSelected}
            onChange={() => onSelect(task.id)}
            aria-label={`Select ${task.title}`}
            onClick={(e) => e.stopPropagation()}
          />
          <div className='task-info-section'>
            <h5 className='task-title'>{task.title}</h5>
            <div className='d-flex align-items-center gap-2 mt-1'>
              {task.status === 'pending' ? (
                <i
                  className={clsx('bi fs-5', statusMeta.icon, statusMeta.className)}
                  title={statusMeta.label}
                ></i>
              ) : (
                <i className='bi bi-check-circle-fill text-success fs-5'></i>
              )}
              <span className='task-status-text'>{statusMeta.label}</span>
            </div>
          </div>
        </div>
        <button
          className='btn btn-action-mobile'
          onClick={handleActionClick}
          aria-label='Actions menu'
          aria-expanded={showActions}
        >
          <i className='fas fa-ellipsis-v'></i>
        </button>
      </div>

      {/* Actions dropdown */}
      {showActions && (
        <div className='actions-dropdown'>
          <button
            className='action-item'
            onClick={() => {
              onEdit(task)
              setShowActions(false)
            }}
          >
            <i className='bi bi-pencil me-2'></i>
            Edit
          </button>
          {onDuplicate && (
            <button
              className='action-item'
              onClick={() => {
                onDuplicate(task)
                setShowActions(false)
              }}
            >
              <i className='bi bi-files me-2'></i>
              Duplicate
            </button>
          )}
          <button
            className='action-item'
            onClick={() => {
              onToggleStatus(task)
              setShowActions(false)
            }}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Spinner size='sm' className='me-2' />
            ) : task.status === 'completed' ? (
              <i className='bi bi-arrow-counterclockwise me-2'></i>
            ) : (
              <i className='bi bi-check2-circle me-2'></i>
            )}
            {task.status === 'completed' ? 'Mark Pending' : 'Mark Completed'}
          </button>
          <button
            className='action-item text-danger'
            onClick={() => {
              onDelete(task)
              setShowActions(false)
            }}
            disabled={isProcessing}
          >
            {isProcessing ? <Spinner size='sm' className='me-2' /> : <i className='bi bi-trash me-2'></i>}
            Delete
          </button>
        </div>
      )}

      {/* Task details grid */}
      <div className='card-details-grid'>
        {/* Description */}
        {task.description && (
          <div className='detail-item full-width'>
            <span className='detail-label'>
              <i className='fas fa-align-left me-1'></i>
              Description
            </span>
            <span className='detail-value'>
              {task.description.length > 120 ? `${task.description.slice(0, 120)}…` : task.description}
            </span>
          </div>
        )}

        {/* Priority */}
        {task.priority && (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-flag me-1'></i>
              Priority
            </span>
            <span
              className={clsx(
                'detail-value task-priority-badge',
                `task-priority-badge--${task.priority.toString().toLowerCase()}`
              )}
            >
              {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
            </span>
          </div>
        )}

        {/* Assigned To */}
        {task.assignedTo ? (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-user me-1'></i>
              Assignee
            </span>
            <span className='detail-value'>
              <div className='d-flex align-items-center gap-2'>
                <span
                  className={clsx(
                    'rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold text-uppercase bg-light-success text-success'
                  )}
                  style={circleBadgeStyle}
                >
                  {formatTaskInitials(task.assignedTo?.name || task.assignedTo?.email)}
                </span>
                <span>{task.assignedTo?.name || task.assignedTo?.email || 'Assignee'}</span>
              </div>
            </span>
          </div>
        ) : (
          <div className='detail-item'>
            <span className='detail-label'>
              <i className='fas fa-user me-1'></i>
              Assignee
            </span>
            <span className='detail-value text-muted'>Unassigned</span>
          </div>
        )}

        {/* Due Date */}
        <div className='detail-item'>
          <span className='detail-label'>
            <i className='fas fa-calendar-alt me-1'></i>
            Due Date
          </span>
          <span className={`detail-value ${isOverdue ? 'text-danger fw-semibold' : ''}`}>
            {formatDateTime(task.dueDate)}
          </span>
        </div>

        {/* Associated Contacts */}
        {contacts.length > 0 && (
          <div className='detail-item full-width'>
            <span className='detail-label'>
              <i className='fas fa-users me-1'></i>
              Associated Contacts
            </span>
            <div className='detail-value'>
              <div className='task-contacts-pills d-flex flex-wrap align-items-center gap-2'>
                {visibleContacts.map((contact, index) => {
                  const label = resolveContactLabel(contact)
                  return (
                    <div
                      key={`${task.id}-contact-${index}`}
                      className='d-inline-flex align-items-center gap-2 border rounded-pill px-2 py-1 bg-light'
                      title={label}
                    >
                      <span
                        className={clsx(
                          'rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold text-uppercase',
                          CONTACT_COLOR_CLASSES[index % CONTACT_COLOR_CLASSES.length]
                        )}
                        style={circleBadgeStyle}
                      >
                        {formatTaskInitials(label)}
                      </span>
                      <span className='fw-semibold text-gray-900'>{label}</span>
                    </div>
                  )
                })}
                {remaining > 0 && (
                  <div className='task-contacts-more rounded-pill bg-light text-gray-700 fw-semibold px-2 py-1'>
                    +{remaining}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

